import * as React from 'react';

import { Campo } from '@/componentes/campo';
import { Button } from '@/componentes/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/componentes/ui/dialog';
import { Input } from '@/componentes/ui/input';
import { mensagemDoErro, useAcessoPorCnpj, type EstadoCnpj } from '@/dados';

/* CNPJ já cadastrado: a barreira antes de nascer um segundo cadastro da mesma
 * empresa.
 *
 * O primeiro preenchimento de um CNPJ é livre e anônimo. O segundo exige
 * conta, e a conta tem de ser no e-mail do primeiro preenchimento. Duas
 * situações, e a diferença entre elas é só se aquele primeiro preenchimento
 * chegou a virar conta:
 *
 *   exige_login     já é conta: entrar nela.
 *   exige_cadastro  ficou anônima: quem provar o e-mail cria a senha e assume.
 *
 * ── POR QUE MODAL, E NÃO UM BLOCO NA TELA ─────────────────────────────────
 * Não é preferência visual: o bloco ficava DENTRO do <form> da etapa, e form
 * aninhado é HTML inválido. O navegador descarta o interno e o botão de
 * "Entrar" virava submit do formulário de fora, que recusa enquanto o CNPJ
 * está ocupado. Resultado: senha certa e senha errada davam exatamente nada.
 * O Dialog do Radix desenha em portal, fora do formulário, e o envio volta a
 * ser o desta caixa.
 *
 * Nenhum dos textos revela o nome da empresa nem o e-mail cadastrado: quem
 * está do outro lado ainda não provou ser ninguém.
 */

const TEXTOS = {
  exige_login: {
    titulo: 'Este CNPJ já tem cadastro',
    apoio: 'Entre com o e-mail usado no cadastro para continuar de onde a empresa parou.',
    botao: 'Entrar',
    rotuloSenha: 'Senha',
    ajudaSenha: undefined,
  },
  exige_cadastro: {
    titulo: 'Este CNPJ já foi preenchido',
    apoio: 'O cadastro ainda não tem conta. Crie a senha usando o mesmo e-mail do primeiro preenchimento.',
    botao: 'Criar conta e continuar',
    rotuloSenha: 'Nova senha',
    ajudaSenha: 'Mínimo de 8 caracteres.',
  },
} as const;

export function CnpjOcupado({
  cnpj,
  estado,
  aberto,
  aoFechar,
  aoEntrar,
}: {
  cnpj: string;
  estado: Extract<EstadoCnpj, 'exige_login' | 'exige_cadastro'>;
  aberto: boolean;
  aoFechar: () => void;
  /** Chamado depois que a sessão passa a ser a dona do CNPJ. */
  aoEntrar: () => void;
}) {
  const texto = TEXTOS[estado];
  const acesso = useAcessoPorCnpj(estado === 'exige_login' ? 'entrar' : 'reivindicar');
  const [email, setEmail] = React.useState('');
  const [senha, setSenha] = React.useState('');

  const enviar = (evento: React.FormEvent) => {
    evento.preventDefault();
    if (acesso.isPending) return;
    acesso.mutate({ cnpj, email, senha }, { onSuccess: () => aoEntrar() });
  };

  return (
    <Dialog open={aberto} onOpenChange={(quer) => !quer && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{texto.titulo}</DialogTitle>
          <DialogDescription>{texto.apoio}</DialogDescription>
        </DialogHeader>

        <form className="grid gap-4" onSubmit={enviar}>
          <Campo rotulo="E-mail do cadastro" obrigatorio>
            {(atributos) => (
              <Input
                {...atributos}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                autoComplete="email"
                required
              />
            )}
          </Campo>

          <Campo
            rotulo={texto.rotuloSenha}
            obrigatorio
            {...(texto.ajudaSenha ? { ajuda: texto.ajudaSenha } : {})}
          >
            {(atributos) => (
              <Input
                {...atributos}
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.currentTarget.value)}
                autoComplete={estado === 'exige_login' ? 'current-password' : 'new-password'}
                minLength={8}
                required
              />
            )}
          </Campo>

          {/* A recusa aparece AQUI, ao lado dos campos que a causaram. É a
              diferença entre "não aconteceu nada" e "a senha está errada". */}
          {acesso.isError ? (
            <p className="text-negativo text-apoio font-medium" role="alert">
              {mensagemDoErro(acesso.error)}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={acesso.isPending}>
              Usar outro CNPJ
            </Button>
            <Button type="submit" disabled={acesso.isPending}>
              {acesso.isPending ? 'Verificando…' : texto.botao}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
